import React, { useEffect, useState } from 'react';
import {
  EnsayoGestion,
  EnsayoRegulado,
  createEnsayoRegulado,
  getMarcosRegulatorios,
  updateEnsayoRegulado,
} from '../services/ensayoService';
import { Button, FormField, Input, Modal, Select } from './ui';

interface Props {
  ensayos: EnsayoGestion[];
  regulado?: EnsayoRegulado;
  onSaved: () => void;
  onCancel: () => void;
}

const EnsayoReguladoForm: React.FC<Props> = ({ ensayos, regulado, onSaved, onCancel }) => {
  const [marcos, setMarcos] = useState<{ id: number; marco: string }[]>([]);
  const [form, setForm] = useState({
    id_ensayo: regulado?.id_ensayo || 0,
    id_marco: regulado?.id_marco || 0,
    valor_min: regulado?.valor_min ?? 0,
    valor_max: regulado?.valor_max ?? 0,
    valor_ref: regulado?.valor_ref ?? 0,
    presencia: regulado?.presencia || '',
    uso: regulado?.uso ?? 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMarcosRegulatorios().then(setMarcos).catch(() => setError('No se pudieron cargar los marcos regulatorios.'));
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.id_ensayo || !form.id_marco) {
      setError('Seleccioná un ensayo y un marco regulatorio.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (regulado) await updateEnsayoRegulado(regulado.id, form);
      else await createEnsayoRegulado(form);
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message || 'No se pudo guardar el ensayo regulado.');
    } finally {
      setLoading(false);
    }
  };

  const setNumber = (name: keyof typeof form, value: string) => setForm(current => ({ ...current, [name]: Number(value) }));

  return (
    <Modal
      title={regulado ? 'Editar ensayo regulado' : 'Crear ensayo regulado'}
      onClose={onCancel}
      size="lg"
      footer={<div className="flex justify-end gap-3"><Button variant="outline" onClick={onCancel}>Cancelar</Button><Button type="submit" form="regulated-form" loading={loading}>Guardar</Button></div>}
    >
      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <form id="regulated-form" onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField label="Ensayo" htmlFor="regulated-test" required className="md:col-span-2">
          <Select id="regulated-test" value={form.id_ensayo} onChange={e => setNumber('id_ensayo', e.target.value)} disabled={!!regulado} required>
            <option value={0}>Seleccionar ensayo</option>
            {ensayos.map(item => <option key={item.id} value={item.id}>{item.analito} — {item.medio_nombre || `Medio ${item.medio}`} ({item.unidad})</option>)}
          </Select>
        </FormField>
        <FormField label="Marco regulatorio" htmlFor="regulated-framework" required className="md:col-span-2">
          <Select id="regulated-framework" value={form.id_marco} onChange={e => setNumber('id_marco', e.target.value)} required>
            <option value={0}>Seleccionar marco</option>
            {marcos.map(item => <option key={item.id} value={item.id}>{item.marco}</option>)}
          </Select>
        </FormField>
        <FormField label="Valor mínimo" htmlFor="regulated-min"><Input id="regulated-min" type="number" step="any" value={form.valor_min} onChange={e => setNumber('valor_min', e.target.value)} /></FormField>
        <FormField label="Valor máximo" htmlFor="regulated-max"><Input id="regulated-max" type="number" step="any" value={form.valor_max} onChange={e => setNumber('valor_max', e.target.value)} /></FormField>
        <FormField label="Valor de referencia" htmlFor="regulated-ref"><Input id="regulated-ref" type="number" step="any" value={form.valor_ref} onChange={e => setNumber('valor_ref', e.target.value)} /></FormField>
        <FormField label="Uso" htmlFor="regulated-use"><Input id="regulated-use" type="number" min="0" value={form.uso} onChange={e => setNumber('uso', e.target.value)} /></FormField>
        <FormField label="Presencia / aclaración" htmlFor="regulated-presence" className="md:col-span-2">
          <Input id="regulated-presence" value={form.presencia} onChange={e => setForm(current => ({ ...current, presencia: e.target.value }))} />
        </FormField>
      </form>
    </Modal>
  );
};

export default EnsayoReguladoForm;
